import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { DatePipe } from '@angular/common';
import { Appointment } from '../../core/models/appointment.model';
import { AppointmentStatus } from '../../core/enums/appointment-status.enum';
import { Router } from '@angular/router';
import { DataStoreService } from '../../core/services/data-store.service';
import { AgePipe } from '../../core/pipes/age.pipe';
import { BookingService } from '../../core/services/booking.service';
import { BookingCardComponent } from './booking-card/booking-card';
import { NotificationService } from '../../core/services/notification.service';
import { User } from '../../core/models/user.model';
import { PortalNavigation, PortalNavigationItem } from '../portal-navigation/portal-navigation';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';

// --- MOCK DATA STRUCTURES (minimal definition for testing) ---
// Using 'as any' casts to keep mocks concise without defining every nested interface fully.

const mockDoctor: any = {
  id: 'doc-1',
  userId: 'user-doc-1',
  licenceNumber: 'L12345',
  specialization: 'CARDIOLOGY',
  experienceYears: 10,
  clinicAddress: '123 Health St, MediCity, 1000',
  user: {
    id: 'user-doc-1',
    firstName: 'Dr. Alex',
    lastName: 'Greene',
    email: 'a.greene@clinic.com',
    phoneNumber: '555-0001',
    title: 'DR',
    gender: 'MALE',
    addressLine1: '123 Health St',
    city: 'MediCity',
    postalCode: '1000',
  },
};
const mockPatient1: any = {
  id: 'pat-1',
  userId: 'user-pat-1',
  firstName: 'Sarah',
  lastName: 'Connor',
  email: 's.connor@email.com',
  phoneNumber: '555-1001',
  dateOfBirth: '1990-05-15',
  gender: 'FEMALE',
  title: 'MRS',
};
const mockPatient2: any = {
  id: 'pat-2',
  userId: 'user-pat-2',
  firstName: 'Kyle',
  lastName: 'Reese',
  email: 'k.reese@email.com',
  phoneNumber: '555-1102',
  dateOfBirth: '1985-11-03',
  gender: 'MALE',
  title: 'MR',
};

// Functions to create ISO date strings for today's appointments
const getTodayTime = (hours: number, minutes: number) =>
  new Date(new Date().setHours(hours, minutes, 0, 0)).toISOString();

const MOCK_APPOINTMENTS: Appointment[] = [
  {
    id: 'appt-001',
    reference: 'R-001',
    doctor: mockDoctor,
    patient: mockPatient1,
    status: AppointmentStatus.Confirmed,
    inProgress: false,
    reasonForVisit: 'Routine check-up for annual physical',
    notes: 'Patient requested follow-up.',
    doctorNotes: '',
    cancellationReason: '',
    cancelledBy: '',
    cancelledDate: null,
    bookingSlot: {
      id: 'slot-1',
      startTime: getTodayTime(10, 0),
      endTime: getTodayTime(10, 30),
      doctorId: 'doc-1',
      patientId: 'pat-1',
      isBooked: true,
    } as any,
    patientDocumentGeneratable: true,
    patientSickNote: {} as any,
    showPrescriptionDocumentOption: true,
    showSickNoteOption: false,
    prescription: {} as any,
  },
  {
    id: 'appt-002',
    reference: 'R-002',
    doctor: mockDoctor,
    patient: mockPatient2,
    status: AppointmentStatus.Pending,
    reasonForVisit: 'Follow-up on recent injury',
    notes: 'Patient had an X-ray last week.',
    doctorNotes: '',
    cancellationReason: '',
    cancelledBy: '',
    cancelledDate: null,
    inProgress: false,
    bookingSlot: {
      id: 'slot-2',
      startTime: getTodayTime(11, 30),
      endTime: getTodayTime(12, 0),
      doctorId: 'doc-1',
      patientId: 'pat-2',
      isBooked: true,
    } as any,
    patientDocumentGeneratable: true,
    patientSickNote: {} as any,
    showPrescriptionDocumentOption: true,
    showSickNoteOption: true,
    prescription: {} as any,
  },
  {
    id: 'appt-003',
    reference: 'R-003',
    doctor: mockDoctor,
    patient: mockPatient1,
    status: AppointmentStatus.Cancelled,
    inProgress: false,
    reasonForVisit: 'General illness and fever',
    notes: 'Appointment was cancelled by patient 2 days prior.',
    doctorNotes: 'Do not rebook within 5 days.',
    cancellationReason: 'Patient recovered quickly',
    cancelledBy: 'PATIENT',
    cancelledDate: getTodayTime(14, 0),
    bookingSlot: {
      id: 'slot-3',
      startTime: getTodayTime(14, 0),
      endTime: getTodayTime(14, 30),
      doctorId: 'doc-1',
      patientId: 'pat-1',
      isBooked: true,
    } as any,
    patientDocumentGeneratable: false,
    patientSickNote: {} as any,
    showPrescriptionDocumentOption: false,
    showSickNoteOption: false,
    prescription: {} as any,
  },
];
// --- END MOCK DATA ---

@Component({
  selector: 'app-booking-list',
  templateUrl: './booking-list.html',
  standalone: true,
  imports: [BookingCardComponent, PortalNavigation],
  styleUrls: ['./booking-list.scss'],
})
export class BookingListComponent implements OnInit {
  private destroyRef = inject(DestroyRef);
  openMenuId: string | null = null;
  // Initialize with mock data for immediate display
  completedBookings: Appointment[] = [];
  ongoingBookings: Appointment[] = [];
  pendingBookings: Appointment[] = [];
  noShowBookings: Appointment[] = [];
  isLoading = true;
  userProfile: User | null = null;
  activeRole = 'DOCTOR';
  readonly clinicNavigationItems: PortalNavigationItem[] = [
    { label: 'Patients', icon: 'bi-people-fill', route: '/dashboard/patients-view' },
    { label: 'Bookings', icon: 'bi-calendar-check-fill', route: '/dashboard/booking-list' },
    { label: 'Create Booking', icon: 'bi-calendar-plus-fill', route: '/dashboard/create-booking' },
  ];
  readonly managerNavigationItems: PortalNavigationItem[] = [
    { label: 'Reporting', icon: 'bi-bar-chart-line', route: '/dashboard/reporting' },
    { label: 'User Management', icon: 'bi-people', route: '/dashboard/manage-users' },
  ];

  // Pagination properties
  bookingsItemsPerPage = 5;
  completedCurrentPage = 1;
  completedPageSize = 0;
  ongoingCurrentPage = 1;
  ongoingPageSize = 0;
  pendingCurrentPage = 1;
  pendingPageSize = 0;
  noShowCurrentPage = 1;
  noShowPageSize = 0;

  constructor(
    private bookingService: BookingService,
    private bookingDataStore: DataStoreService,
    private notificationService: NotificationService,
    private router: Router,
  ) {}

  ngOnInit(): void {
    this.loadUserProfile();
    this.loadBookings();
  }

  loadBookings(): void {
    this.isLoading = true;
    forkJoin({
      completed: this.bookingService.getPatientBookingsByStatus(AppointmentStatus.Completed).pipe(
        catchError((error) => {
          this.notificationService.error(error?.error?.message || 'Failed to load completed bookings');
          return of([]);
        }),
      ),
      pending: this.bookingService.getPatientBookingsByStatus(AppointmentStatus.Pending).pipe(
        catchError((error) => {
          this.notificationService.error(error?.error?.message || 'Failed to load pending bookings');
          return of([]);
        }),
      ),
      ongoing: this.bookingService.getPatientBookingsByStatus(AppointmentStatus.Ongoing).pipe(
        catchError((error) => {
          this.notificationService.error(error?.error?.message || 'Failed to load ongoing bookings');
          return of([]);
        }),
      ),
      confirmed: this.bookingService.getPatientBookingsByStatus(AppointmentStatus.Confirmed).pipe(
        catchError((error) => {
          this.notificationService.error(error?.error?.message || 'Failed to load confirmed bookings');
          return of([]);
        }),
      ),
      noShow: this.bookingService.getPatientBookingsByStatus(AppointmentStatus.NoShow).pipe(
        catchError((error) => {
          this.notificationService.error(error?.error?.message || 'Failed to load no-show bookings');
          return of([]);
        }),
      ),
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.completedBookings = res.completed;
          this.pendingBookings = res.pending;
          this.ongoingBookings = [...res.ongoing, ...res.confirmed];
          this.noShowBookings = res.noShow;
          this.isLoading = false;
        },
        error: () => {
          this.isLoading = false;
        },
      });
  }

  getStatusClass(status: AppointmentStatus): string {
    switch (status) {
      case AppointmentStatus.Confirmed:
        return 'status-scheduled';
      case AppointmentStatus.Pending:
        return 'status-pending';
      case AppointmentStatus.Cancelled:
        return 'status-cancelled';
      case AppointmentStatus.NoShow:
        return 'status-no-show';
      case AppointmentStatus.Completed:
        return 'status-completed';
      default:
        return 'status-scheduled';
    }
  }

  // Pagination getters and methods
  get paginatedCompletedBookings(): Appointment[] {
    const startIndex = (this.completedCurrentPage - 1) * this.bookingsItemsPerPage;
    return this.completedBookings.slice(startIndex, startIndex + this.bookingsItemsPerPage);
  }

  get paginatedOngoingBookings(): Appointment[] {
    const startIndex = (this.ongoingCurrentPage - 1) * this.bookingsItemsPerPage;
    return this.ongoingBookings.slice(startIndex, startIndex + this.bookingsItemsPerPage);
  }

  get paginatedPendingBookings(): Appointment[] {
    const startIndex = (this.pendingCurrentPage - 1) * this.bookingsItemsPerPage;
    return this.pendingBookings.slice(startIndex, startIndex + this.bookingsItemsPerPage);
  }

  get paginatedNoShowBookings(): Appointment[] {
    const startIndex = (this.noShowCurrentPage - 1) * this.bookingsItemsPerPage;
    return this.noShowBookings.slice(startIndex, startIndex + this.bookingsItemsPerPage);
  }

  completedPreviousPage(): void {
    if (this.completedCurrentPage > 1) this.completedCurrentPage--;
  }

  completedNextPage(): void {
    if (this.completedCurrentPage < this.completedPageSize) this.completedCurrentPage++;
  }

  ongoingPreviousPage(): void {
    if (this.ongoingCurrentPage > 1) this.ongoingCurrentPage--;
  }

  ongoingNextPage(): void {
    if (this.ongoingCurrentPage < this.ongoingPageSize) this.ongoingCurrentPage++;
  }

  pendingPreviousPage(): void {
    if (this.pendingCurrentPage > 1) this.pendingCurrentPage--;
  }

  pendingNextPage(): void {
    if (this.pendingCurrentPage < this.pendingPageSize) this.pendingCurrentPage++;
  }

  noShowPreviousPage(): void {
    if (this.noShowCurrentPage > 1) this.noShowCurrentPage--;
  }

  noShowNextPage(): void {
    if (this.noShowCurrentPage < this.noShowPageSize) this.noShowCurrentPage++;
  }

  canCompleteBooking(booking: Appointment): boolean {
    return this.activeRole === 'DOCTOR' && booking.doctor?.user?.id === this.userProfile?.id;
  }

  toggleMenu(id: string) {
    this.openMenuId = this.openMenuId === id ? null : id;
  }

  viewDetails(booking: Appointment) {
    this.bookingDataStore.setBooking(booking);

    this.router
      .navigate(['dashboard/view-booking'], {
        state: { booking: booking },
      })
      .then((r) => {});
    // route to booking details or open modal
  }

  startBooking(booking: Appointment) {
    if (!!booking) {
      this.isLoading = true;
      this.bookingService
        .startBooking(booking?.id)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: () => {
            forkJoin({
              ongoing: this.bookingService.getPatientBookingsByStatus(AppointmentStatus.Ongoing).pipe(catchError(() => of([]))),
              confirmed: this.bookingService.getPatientBookingsByStatus(AppointmentStatus.Confirmed).pipe(catchError(() => of([]))),
            })
              .pipe(takeUntilDestroyed(this.destroyRef))
              .subscribe({
                next: (res) => {
                  this.ongoingBookings = [...res.ongoing, ...res.confirmed];
                  this.isLoading = false;
                },
                error: () => (this.isLoading = false),
              });
          },
          error: (error) => {
            this.isLoading = false;
            this.notificationService.error(error?.error?.message || 'Failed to start booking');
          },
        });
    }
  }

  cancelBooking(booking: Appointment) {
    if (!booking || !booking.id) {
      return;
    }
    this.isLoading = true;
    this.bookingService
      .markNoShow(booking)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (updated) => {
          this.ongoingBookings = this.ongoingBookings.filter((b) => b.id !== booking.id);
          this.pendingBookings = this.pendingBookings.filter((b) => b.id !== booking.id);
          this.completedBookings = this.completedBookings.filter((b) => b.id !== booking.id);

          const noShowItem: Appointment = updated
            ? updated
            : { ...booking, status: AppointmentStatus.NoShow };
          this.noShowBookings = [
            noShowItem,
            ...this.noShowBookings.filter((b) => b.id !== booking.id),
          ];
          this.isLoading = false;
          this.notificationService.success('Appointment cancelled and moved to No-Show queue');
        },
        error: (error) => {
          this.isLoading = false;
          this.notificationService.error(error?.error?.message || 'Failed to cancel appointment');
        },
      });
  }

  completeBooking(booking: Appointment) {
    this.isLoading = true;
    this.bookingService
      .completeBooking(booking)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.ongoingBookings = this.ongoingBookings.filter((b) => b.id !== booking.id);
          this.bookingService
            .getPatientBookingsByStatus(AppointmentStatus.Completed)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((data) => {
              this.completedBookings = data;
              this.isLoading = false;
            });
        },
        error: (error) => {
          this.isLoading = false;
          this.notificationService.error(error?.error?.message || 'Failed to complete booking');
        },
      });
  }

  noShowBooking(booking: Appointment) {
    if (!booking || !booking.id) {
      return;
    }
    this.isLoading = true;
    this.bookingService
      .markNoShow(booking)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (updated) => {
          this.ongoingBookings = this.ongoingBookings.filter((b) => b.id !== booking.id);
          this.pendingBookings = this.pendingBookings.filter((b) => b.id !== booking.id);
          this.completedBookings = this.completedBookings.filter((b) => b.id !== booking.id);

          const noShowItem: Appointment = updated
            ? updated
            : { ...booking, status: AppointmentStatus.NoShow };
          this.noShowBookings = [
            noShowItem,
            ...this.noShowBookings.filter((b) => b.id !== booking.id),
          ];
          this.isLoading = false;
          this.notificationService.success('Appointment marked as No-Show');
        },
        error: (error) => {
          this.isLoading = false;
          this.notificationService.error(error?.error?.message || 'Failed to mark as no-show');
        },
      });
  }

  onImgError(event: Event) {
    (event.target as HTMLImageElement).src = 'person-image.jpg';
  }

  get portalName(): string {
    return `${this.activeRole.charAt(0)}${this.activeRole.slice(1).toLowerCase()} Portal`;
  }

  get portalIcon(): string {
    return this.activeRole === 'MANAGER'
      ? 'bi-clipboard2-pulse-fill'
      : this.activeRole === 'RECEPTION'
        ? 'bi-hospital-fill'
        : 'bi-heart-pulse-fill';
  }

  get portalClass(): string {
    return this.activeRole === 'MANAGER' ? 'text-danger' : 'text-primary';
  }

  get profileRoute(): string {
    return this.activeRole === 'DOCTOR' ? '/dashboard/doctor-profile' : '/dashboard/profile';
  }

  get primaryNavigationItems(): PortalNavigationItem[] {
    return this.activeRole === 'MANAGER' ? [] : this.clinicNavigationItems;
  }

  get additionalNavigationItems(): PortalNavigationItem[] {
    return this.activeRole === 'MANAGER' ? this.managerNavigationItems : [];
  }

  private loadUserProfile(): void {
    const storedProfile = localStorage.getItem('userProfile');
    this.activeRole = localStorage.getItem('userRole') ?? 'DOCTOR';

    if (!storedProfile) {
      return;
    }

    try {
      this.userProfile = JSON.parse(storedProfile) as User;
    } catch {
      this.userProfile = null;
    }
  }

  protected readonly AppointmentStatus = AppointmentStatus;
}
